import { makeAutoObservable, toJS, runInAction } from "mobx";
import { RouteType, RouteParamsType, RouteQueryParamsType } from "types/RouteType";

export interface HistoryEntry {
    route: { key?: string };
    params: RouteParamsType;
    queryParams?: RouteQueryParamsType;
    timestamp: number;
}

/**
 * Manages browser history and navigation history.
 * Follows Single Responsibility Principle by focusing only on history management.
 */
export class HistoryManager {
    // Observable state
    routerHistory: HistoryEntry[] = [];
    private maxHistorySize = 50; // Prevent memory leaks with large history

    constructor() {
        makeAutoObservable(this);
    }

    /**
     * Records a new entry in the navigation history
     * @param route - Route that was navigated to
     * @param params - Parameters used in the navigation
     */
    recordHistory({ route, params, queryParams }: { route: RouteType; params: RouteParamsType; queryParams?: RouteQueryParamsType }): void {
        const historyEntry: HistoryEntry = {
            route: { key: route.key },
            params: toJS(params),
            queryParams: queryParams ? toJS(queryParams) : undefined,
            timestamp: Date.now()
        };

        this.routerHistory.push(historyEntry);

        // Limit history size to prevent memory issues
        if (this.routerHistory.length > this.maxHistorySize) {
            this.routerHistory.shift();
        }
    }

    /**
     * Removes and returns the last history entry
     * @returns Last history entry or null if history is empty
     */
    popLastHistory(): HistoryEntry | null {
        return this.routerHistory.pop() || null;
    }

    /**
     * Gets the previous navigation entry without removing it
     * @returns Previous history entry or null if no previous entry
     */
    getPreviousEntry(): HistoryEntry | null {
        return this.routerHistory.length > 1
            ? this.routerHistory[this.routerHistory.length - 2]
            : null;
    }

    /**
     * Clears all navigation history
     */
    clearHistory(): void {
        runInAction(() => {
            this.routerHistory = [];
        });
    }

    /**
     * Gets the current history length
     * @returns Number of entries in history
     */
    getHistoryLength(): number {
        return this.routerHistory.length;
    }

    /**
     * Checks if navigation can go back
     * @returns True if there's a previous entry to navigate to
     */
    canGoBack(): boolean {
        return this.routerHistory.length > 1;
    }

    /**
     * Gets a copy of the current history for debugging or analysis
     * @returns Array of history entries
     */
    getHistoryCopy(): HistoryEntry[] {
        return [...this.routerHistory];
    }

    /**
     * Finds the most recent entry for a specific route
     * @param routeKey - Route key to search for
     * @returns Most recent history entry for the route or null
     */
    findMostRecentEntryForRoute(routeKey: string): HistoryEntry | null {
        for (let i = this.routerHistory.length - 1; i >= 0; i--) {
            const entry = this.routerHistory[i];
            if (entry.route.key === routeKey) {
                return entry;
            }
        }
        return null;
    }

    /**
     * Gets navigation history for a specific time period
     * @param hoursAgo - Number of hours to look back
     * @returns Array of history entries from the specified time period
     */
    getHistoryFromPeriod(hoursAgo: number): HistoryEntry[] {
        const cutoffTime = Date.now() - (hoursAgo * 60 * 60 * 1000);
        return this.routerHistory.filter(entry => entry.timestamp >= cutoffTime);
    }
}
